import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
	AbstractModal,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType,
	ToasterType
} from '@cv-invision/core';
import { Store } from '@ngxs/store';
import { AceEditorComponent } from 'ngx-ace-editor-wrapper';
import { NexsysMonitorService } from '../../../service/nexsys-monitor.service';
import { ACTIONENDPOINT, ORIGIN, URLS } from '../../model/nexsys-monitor.constant';
import { NexsysOrder } from '../../state/model/nexsys-monitor-state.model';
import { NexsysMonitorState } from '../../state/nexsys-monitor.state';
import * as vkbeautify from 'vkbeautify';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'cv-retry-nexsys-event',
	templateUrl: './retry-nexsys-event.component.html',
	styleUrls: ['./retry-nexsys-event.component.scss']
})
export class RetryNexsysEventComponent extends AbstractModal implements OnInit, OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	@ViewChild('markupEditor', { static: false }) markupEditor: AceEditorComponent;
	saving: boolean;
	orderData: NexsysOrder;
	methodUrl: string;
	method: string;
	responseInformation: string;
	orderId: string;
	serviceId: string;
	source: string;
	apiVersion = null;

	constructor(
		private store: Store,
		private nexsysMonitor: NexsysMonitorService,
		private broadcastService: BroadcastService,
		private router: Router
	) {
		super();
		this.method = 'POST';
	}

	ngOnInit(): void {
		this.orderData = this.store.selectSnapshot(NexsysMonitorState.getNexsysOrderInfoBase);

		switch (this.data.source) {
			case ORIGIN.Nexsys:
				this.source = ORIGIN.Nexsys;
				this.orderId = this.orderData.nexsysOrderId;
				this.apiVersion = '2';
				break;
			case ORIGIN.Valutrust:
				this.source = ORIGIN.Valutrust;
				this.orderId = this.orderData.valutrustOrderId;
				break;
			case ORIGIN.Xpanse:
				this.source = ORIGIN.Xpanse;
				this.orderId = this.orderData.xpanseSPNId;
				break;
			case ORIGIN.OrderExternal:
				this.source = ORIGIN.OrderExternal;
				this.orderId = this.orderData.classOrderId;
				break;
			case ORIGIN.FNC:
				this.source = ORIGIN.FNC;
				this.orderId = this.orderData.fncOrderId;
				break;
			case ORIGIN.EVO:
				this.source = ORIGIN.EVO;
				this.orderId = this.orderData.evoOrderId;
				this.serviceId = this.orderData.evoServiceId;
				break;
			case ORIGIN.ValueLink:
				this.source = ORIGIN.ValueLink;
				this.orderId = this.orderData.classOrderId;
				this.serviceId = this.orderData.valueLinkServiceId;
				break;
			case ORIGIN.Connexions:
				this.source = ORIGIN.Connexions;
				this.orderId = this.orderData.classOrderId;
				this.serviceId = this.orderData.valueLinkServiceId;
				break;
			case ORIGIN.Lendingqb:
				this.source = ORIGIN.Lendingqb;
				this.orderId = this.orderData.classOrderId;
				this.serviceId = this.orderData.valueLinkServiceId;
				break;
		}

		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};
		const confirmButton: ModalButtonModel = {
			name: `Send to ${this.source}`,
			text: `Send to ${this.source}`,
			class: 'btn-sm cv-btn-blue ',
			type: ModalButtonType.ACTION,
			disabled: (): boolean => !this.setIsFormValid() || this.saving,
			action: () => this.onSave(),
			loading: (): boolean => this.saving,
			loadingText: 'Sending'
		};
		this.buttons.push(cancelButton);
		this.buttons.push(confirmButton);
		this.responseInformation = this.data.request? this.parseJsonData(this.data.request) : this.parseJsonData(this.data.messageRequest);
		this.setIntegrationEndpointMethod(this.data.action, this.source);
	}
	/**
	 * parse josn strin from the server
	 * @param info
	 */
	parseJsonData(info: string): string {
		try {
			return JSON.stringify(JSON.parse(info), null, 3);
		} catch (e) {
			const data = JSON.parse(JSON.stringify(info, null, 1));
			return vkbeautify.xml(data);
		}
	}

	/**
	 * retry nexsys payload
	 */
	onSave(): void {
		if (this.method === 'POST') {
			const id = this.nexsysMonitor
				.retryNexsysOrderTransations(
					this.orderId,
					this.methodUrl,
					this.responseInformation,
					this.source,
					this.serviceId,
					this.apiVersion
				)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(
					response => {
						this.successHandleFunction();
						super.onAcceptSuccess({
							success: true
						});
					},
					error => {
						this.errorMsgFunction(error.error);
						super.onCancel();
					}
				);
		}
		if (this.method === 'PUT') {
			this.nexsysMonitor
				.retryUpdateNexsysOrderTransations(
					this.orderId,
					this.methodUrl,
					this.responseInformation,
					this.source
				)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(
					response => {
						this.successHandleFunction();
						super.onAcceptSuccess({
							success: true
						});
					},
					error => {
						this.errorMsgFunction(error.error);
						super.onCancel();
					}
				);
		}
	}

	/**
	 * base on the logAction set what endpoint should the action call
	 */
	setIntegrationEndpointMethod(action: string, source: string): void {
		switch (action) {
			case 'ASSIGNED':
				this.methodUrl = ACTIONENDPOINT.ASSIGNED;
				break;
			case 'ASSIGNED-TO-APPAISER':
				this.methodUrl = ACTIONENDPOINT.ASSIGNED_TO_APPRAISER;
				break;
			case 'APPOINTMENT-SCHEDULED':
				this.methodUrl = ACTIONENDPOINT.APPOINTMENT_SCHEDULED;
				break;
			case 'REVISION':
				this.methodUrl = ACTIONENDPOINT.REVISION;
				break;
			case 'INSPECTION_COMPLETE':
			case 'INSPECTION-COMPLETE':
				this.methodUrl = ACTIONENDPOINT.INSPECTION_COMPLETE;
				break;
			case 'INSPECTIONCOMPLETED':
				this.methodUrl = ACTIONENDPOINT.INSPECTION_COMPLETED;
				break;
			case 'COMPLETED-INSPECTION':
				this.methodUrl = ACTIONENDPOINT.COMPLETED_INSPECTION;
				break;

			case 'INSPECTION_SCHEDULE':
				this.methodUrl = ACTIONENDPOINT.INSPECTION_SCHEDULE;
				break;
			case 'SCHEDULE_INSPECTION':
				this.methodUrl = ACTIONENDPOINT.SCHEDULE_INSPECTION;
				break;
			case 'ASSIGNVENDOR':
				this.methodUrl = ACTIONENDPOINT.ASSIGNVENDOR;
				break;
			case 'OFFHOLD':
				this.methodUrl = ACTIONENDPOINT.OFFHOLD;
				break;
			case 'ACCEPTED':
				this.methodUrl = ACTIONENDPOINT.ACCEPTED;
				break;
			case 'AMC_ACCEPTED':
				this.methodUrl = ACTIONENDPOINT.AMC_ACCEPTED;
				break;
			case 'ACCEPT': 
				this.methodUrl = ACTIONENDPOINT.ACCEPT;
				break; 
			case 'ACCEPTED_WITH_CONDITIONS':
				this.methodUrl = ACTIONENDPOINT.ACCEPTED_WITH_CONDITIONS;
				break;
			case 'ACCEPT-WITH-CONDITIONS':
			case 'ACCEPT_WITH_CONDITIONS':
				this.methodUrl = ACTIONENDPOINT.ACCEPT_WITH_CONDITIONS;
				break;
			case 'ADD_DOCUMENT':
				this.methodUrl = ACTIONENDPOINT.ADD_DOCUMENT;
				break;
			case 'ADD_COMMENT':
				this.methodUrl = ACTIONENDPOINT.ADD_COMMENT;
				break;
			case 'ADD_REPORT':
				this.methodUrl = ACTIONENDPOINT.ADD_REPORT;
				break;
			case 'ADD-ATTACHMENT':
			case 'ADD_ATTACHMENT':
				this.methodUrl = ACTIONENDPOINT.ADD_ATTACHMENT;
				break;
			case 'ADD-NOTE':
			case 'ADD_NOTE':
				this.methodUrl = ACTIONENDPOINT.ADD_NOTE;
				break;	
			case 'FAILED':
				this.methodUrl = ACTIONENDPOINT.FAILED;
				break;
			case 'UNDERREVIEW':
				this.methodUrl = ACTIONENDPOINT.UNDERREVIEW;
				break;
			case 'NOTE':
				this.methodUrl = ACTIONENDPOINT.NOTE;
				break;
			case 'COMPLETED':
				this.methodUrl = ACTIONENDPOINT.COMPLETED;
				break;
			case 'COMPLETE':
				this.methodUrl = ACTIONENDPOINT.COMPLETE;
				break;
			case 'COMPLETION_DATE':
				this.methodUrl = ACTIONENDPOINT.COMPLETION_DATE;
				break;
			case 'APPOINTMENTCREATED':
				this.methodUrl = ACTIONENDPOINT.APPOINTMENTCREATED;
				break;
			case 'APPOINTMENTUPDATED':
				this.methodUrl = ACTIONENDPOINT.APPOINTMENTUPDATED;
				this.method = 'PUT';
				break;
			case 'APPRAISALASSIGNED':
				this.methodUrl = ACTIONENDPOINT.APPRAISALASSIGNED;
				break;
			case 'ONHOLD':
				if (source === ORIGIN.Nexsys) {
					this.methodUrl = ACTIONENDPOINT.ON_HOLD;
				} else {
					this.methodUrl = ACTIONENDPOINT.ONHOLD;
				}
				break;
			case 'UPLOADDOCUMENT':
			case 'DOCUMENTS':
				this.methodUrl = ACTIONENDPOINT.UPLOADDOCUMENT;
				break;
			case 'REFUND':
				this.methodUrl = ACTIONENDPOINT.REFUND;
				break;
			case 'REVISIONCLEARED':
				this.methodUrl = ACTIONENDPOINT.REVISIONCLEARED;
				break;
			case 'REVISIONSCOMPLETEDNODOC':
				this.methodUrl = ACTIONENDPOINT.REVISIONSCOMPLETEDNODOC;
				break;
			case 'REVISION-RECEIVED':
				this.methodUrl = ACTIONENDPOINT.REVISION_RECEIVED;
				break;
			
			case 'REVISIONSCOMPLETEDDOC':
				this.methodUrl = ACTIONENDPOINT.REVISIONSCOMPLETEDDOC;
				break;
			case 'REVISIONREQUIRED':
				this.methodUrl = ACTIONENDPOINT.REVISION_REQUIRED;
				break;
			case 'NOTIFYDUEDATE':
				this.methodUrl = ACTIONENDPOINT.NOTIFYDUEDATE;
				break;
			case 'CANCEL':
				this.methodUrl = ACTIONENDPOINT.CANCEL;
				break;
			case 'CANCELLED':
				this.methodUrl = ACTIONENDPOINT.CANCELLED;
				break;
			case 'DECLINE':
					this.methodUrl = ACTIONENDPOINT.DECLINE;
					break;
			case 'DECLINED':
				this.methodUrl = ACTIONENDPOINT.DECLINED;
				break;
			case 'NOTIFYAPPOINTMENT':
				this.methodUrl = ACTIONENDPOINT.NOTIFYAPPOINTMENT;
				break;
			case 'RESUME':
				this.methodUrl = ACTIONENDPOINT.RESUME;
				break;
			case 'RESUME_ONHOLD':
				this.methodUrl = ACTIONENDPOINT.RESUME_ONHOLD;
				break;
			case 'RESUME_ON_HOLD':
				this.methodUrl = ACTIONENDPOINT.RESUME_ON_HOLD;
				break;
			case 'ON_HOLD':
				this.methodUrl = ACTIONENDPOINT.ONHOLD;
				break;
			case 'NOTECOMPLETED':
				this.methodUrl = ACTIONENDPOINT.NOTECOMPLETED;
				break;
			// Start endpoint xpanse update status
			case 'UPDATESTATUS_CANCEL':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_CANCEL;
				break;
			case 'UPDATESTATUS_CANCEL_CONFIRMED':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_CANCEL_CONFIRMED;
				break;
			case 'UPDATESTATUS_DELIVER_PRODUCT':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_DELIVER_PRODUCT;
				break;
			case 'UPDATESTATUS_DENY_ORDER':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_DENY_ORDER;
				break;
			case 'UPDATESTATUS_ESCALATION':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_ESCALATION;
				break;
			case 'UPDATESTATUS_ESCALATION_RESPONSE':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_ESCALATION_RESPONSE;
				break;
			case 'UPDATESTATUS_REOPEN_APPROVED':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REOPEN_APPROVED;
				break;
			case 'UPDATESTATUS_REOPEN_REQUEST':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REOPEN_REQUEST;
				break;
			case 'UPDATESTATUS_QC_CORRECTION_COMPLETE':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_QC_CORRECTION_COMPLETE;
				break;
			case 'UPDATESTATUS_QC_CORRECTION_PENDING':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_QC_CORRECTION_PENDING;
				break;
			case 'UPDATESTATUS_REQUEST_CANCELLATION':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REQUEST_CANCELLATION;
				break;
			case 'UPDATESTATUS_REQUEST_REVISION':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REQUEST_REVISION;
				break;
			case 'UPDATESTATUS_RESUME':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_RESUME;
				break;
			case 'UPDATESTATUS_REVISED_DELIVER_PRODUCT':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REVISED_DELIVER_PRODUCT;
				break;
			case 'UPDATESTATUS_REVISION_REQUEST_APPROVED':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REVISION_REQUEST_APPROVED;
				break;
			case 'UPDATESTATUS_REVISION_REQUEST_NOT_APPROVED':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_REVISION_REQUEST_NOT_APPROVED;
				break;
			case 'UPDATESTATUS_SUSPEND_ORDER':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS_SUSPEND_ORDER;
				break;
			case 'UPDATESTATUS':
				this.methodUrl = ACTIONENDPOINT.UPDATESTATUS;
				break;
			case 'CALLBACK_UPDASTATUS_CANCEL':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_CANCEL;
				break;
			case 'CALLBACK_UPDASTATUS_CANCEL_CONFIRMED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_CANCEL_CONFIRMED;
				break;
			case 'CALLBACK_UPDASTATUS_DELIVER_PRODUCT':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_DELIVER_PRODUCT;
				break;
			case 'CALLBACK_UPDASTATUS_DENY_ORDER':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_DENY_ORDER;
				break;
			case 'CALLBACK_UPDASTATUS_ESCALATION':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_ESCALATION;
				break;
			case 'CALLBACK_UPDASTATUS_ESCALATION_RESPONSE':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_ESCALATION_RESPONSE;
				break;
			case 'CALLBACK_UPDASTATUS_REOPEN_APPROVED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REOPEN_APPROVED;
				break;
			case 'CALLBACK_UPDASTATUS_REOPEN_REQUEST':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REOPEN_REQUEST;
				break;
			case 'CALLBACK_FEEMODIFICATIONAPPROVED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_FEEMODIFICATIONAPPROVED;
				break;
			case 'CALLBACK_UPDASTATUS_QC_CORRECTION_COMPLETE':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_QC_CORRECTION_COMPLETE;
				break;
			case 'CALLBACK_UPDASTATUS_QC_CORRECTION_PENDING':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_QC_CORRECTION_PENDING;
				break;
			case 'CALLBACK_UPDASTATUS_REQUEST_CANCELLATION':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REQUEST_CANCELLATION;
				break;
			case 'CALLBACK_UPDASTATUS_REQUEST_REVISION':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REQUEST_REVISION;
				break;
			case 'CALLBACK_UPDASTATUS_RESUME':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_RESUME;
				break;
			case 'CALLBACK_UPDASTATUS_REVISED_DELIVER_PRODUCT':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REVISED_DELIVER_PRODUCT;
				break;
			case 'CALLBACK_UPDASTATUS_REVISED_REQUEST_APPROVED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REVISED_REQUEST_APPROVED;
				break;
			case 'CALLBACK_UPDASTATUS_REVISED_REQUEST_NOT_APPROVED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_REVISED_REQUEST_NOT_APPROVED;
				break;
			case 'CALLBACK_UPDASTATUS_SUSPEND_ORDER':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_SUSPEND_ORDER;
				break;
			case 'CALLBACK_UPDASTATUS_VENDOR_QC':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS_VENDOR_QC;
				break;
			case 'CALLBACK_UPDASTATUS':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDASTATUS;
				break;
			// End endpoint xpanse update status
			case 'ADDATTACHMENT':
				this.methodUrl = ACTIONENDPOINT.ADDATTACHMENT;
				break;
			case 'ASSIGNED_INFORMATION':
				this.methodUrl = ACTIONENDPOINT.ASSIGNED_INFORMATION;
				break;
			case 'REQUEST_FHA_NUMBER':
				this.methodUrl = ACTIONENDPOINT.REQUEST_FHA_NUMBER;
				break;
			case 'SCHEDULE':
				this.methodUrl = ACTIONENDPOINT.SCHEDULE;
				break;
			case 'SCHEDULED':
				this.methodUrl = ACTIONENDPOINT.SCHEDULED;
				break;
			case 'ADDNOTE':
				this.methodUrl = ACTIONENDPOINT.ADDNOTE;
				break;
			case 'INSPECTED':
				this.methodUrl = ACTIONENDPOINT.INSPECTED;
				break;
			case 'FEE_MODIFICATION':
				this.methodUrl = ACTIONENDPOINT.FEE_MODIFICATION;
				break;
			case 'FINAL_INVOICE':
				this.methodUrl = ACTIONENDPOINT.FINAL_INVOICE;
				break;
			case 'FULLCOMPLETED':
				this.methodUrl = ACTIONENDPOINT.FULLCOMPLETED;
				break;
			case 'CALLBACK_NEWDOCUMENT':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_NEWDOCUMENT;
				break;
			case 'CALLBACK_PLACEORDER':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_PLACEORDER;
				break;
			case 'CALLBACK_EXPEDITEORDERAPPROVED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_EXPEDITEORDERAPPROVED;
				break;
			case 'CALLBACK_NOTE':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_NOTE;
				break;
			case 'CALLBACK_NOTE_ACK':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_NOTE_ACK;
				break;
			case 'CALLBACK_ORDERUPDATED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_ORDERUPDATED;
				break;
			case 'CALLBACK_FEE_MODIFICATION_NOT_APPROVED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_FEE_MODIFICATION_NOT_APPROVED;
				break;
			case 'CALLBACK_UPDATESTATUS_COMPLETED':
			case 'CALLBACK_UPDATESTATUS_ACTIVE':
			case 'CALLBACK_UPDATESTATUS_RESUME':
			case 'CALLBACK_UPDATESTATUS_ONHOLD':
			case 'CALLBACK_UPDATESTATUS_CANCELLED':
			case 'CALLBACK_UPDATESTATUS_UNDER':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPDATESTATUS;
				break;
			case 'CALLBACK_UPLOADDOCUMENTS':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_UPLOADDOCUMENTS;
				break;
			case 'CALLBACK_SETAPPOINMENT':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_SETAPPOINMENT;
				break;
			case 'CALLBACK_NOTES':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_NOTES;
				break;
			case 'CALLBACK_SCANNER':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_SCANNER;
				break;
			case 'CALLBACK_DESKTOP_EVENTS':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_DESKTOP_EVENTS;
				break;
			case 'CALLBACK_ASSIGNED_TO_VENDOR':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_ASSIGNED_TO_VENDOR;
				break;
			case 'CALLBACK_CLIENT_FEE_CHANGED':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_CLIENT_FEE_CHANGED;
				break;
			case 'CALLBACK_CLIENT_DUE_DATE':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_CLIENT_DUE_DATE;
				break;
			case 'CALLBACK_PAYMENT_LINK_SENT_BORROWER':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_PAYMENT_LINK_SENT_BORROWER;
				break;
			case 'CALLBACK_CUSTOM_FIELD_SET':
				this.methodUrl = ACTIONENDPOINT.CALLBACK_CUSTOM_FIELD_SET;
				break;
			case 'MODIFICATION_REQUEST':
				this.methodUrl = ACTIONENDPOINT.MODIFICATION_REQUEST;
				break;
			case 'PAYMENT-RECEIVED':
				this.methodUrl = ACTIONENDPOINT.PAYMENT_RECEIVED;
				break;
			case 'SATISFY-CONDITION-WITH-COMMENT':
				this.methodUrl = ACTIONENDPOINT.SATISFY_CONDITION_WITH_COMMENT;
				break;
			case 'SATISFY-CONDITION-WITH-FILE':
				this.methodUrl = ACTIONENDPOINT.SATISFY_CONDITION_WITH_FILE;
				break;
			case 'FEECHANGE':
				this.methodUrl = ACTIONENDPOINT.FEE_CHANGE;
				break;
			case 'ROV':
				this.methodUrl = ACTIONENDPOINT.ROV;
				break;
			case 'PROJECTEDCOMPLETIONDATE':
				this.methodUrl = ACTIONENDPOINT.PROJECTEDCOMPLETIONDATE;
				break;
			case 'ACCEPTED-BY-APPRAISER':
				this.methodUrl = ACTIONENDPOINT.PROJECTEDCOMPLETIONDATE;
				break;
			case 'PLACE-ORDER-FAILED':
				this.methodUrl = ACTIONENDPOINT.PLACE_ORDER_FAILED;
				break;
			}
	}

	/**
	 * Is form valid?
	 */
	setIsFormValid(): boolean {
		return true;
	}

	/**
	 * handle response from server
	 */
	successHandleFunction(): void {
		const msg = `The action was successfully sent to ${this.source}`;
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: { message: msg }
		});
	}

	/**
	 * Display error message wheter the oparation wast´n success
	 * @param error
	 */
	errorMsgFunction(error: any): void {
		const { Error } = error;

		const event = {
			error: Error ? Error.split(':')[3].replace(/["}]/g, '') : 'Something went wrong please try again',
			autoClose: true
		};
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: event
		});
	}

	onDismiss(): void {
		super.onCancel();
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
